import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '@/i18n/LanguageContext';
import type { LanguageCode } from '@/i18n/languages';

/**
 * Pop-over language picker for the nav bar. Mirrors <ThemeSelector>'s a11y
 * pattern: a trigger button opens a role="menu" of role="menuitemradio"
 * options; Esc + outside-click close it. Option labels are the native
 * language names (never translated); the selector's own chrome uses t().
 */
export function LanguageSelector(): JSX.Element {
  const { t } = useTranslation();
  const { language, languages, setLanguage } = useLanguage();
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  const current = languages.find((l) => l.code === language) ?? languages[0];

  useEffect(() => {
    if (!open) return;
    const onPointer = (e: PointerEvent): void => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('pointerdown', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const choose = (code: LanguageCode): void => {
    setLanguage(code);
    setOpen(false);
  };

  return (
    <div className="language-selector" ref={rootRef}>
      <button
        type="button"
        className="icon-btn language-selector__trigger"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`${t('language.selectorLabel')}: ${current.label}. ${t('language.openMenu')}`}
        onClick={() => setOpen((o) => !o)}
      >
        <GlobeIcon />
      </button>

      {open && (
        <div role="menu" aria-label={t('language.menuLabel')} className="language-selector__menu">
          {languages.map((l) => {
            const selected = l.code === language;
            return (
              <button
                key={l.code}
                type="button"
                role="menuitemradio"
                aria-checked={selected}
                className={`language-selector__item ${selected ? 'is-selected' : ''}`}
                onClick={() => choose(l.code)}
              >
                <span className="language-selector__name">{l.label}</span>
                {selected && <span className="language-selector__check" aria-hidden>✓</span>}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

function GlobeIcon(): JSX.Element {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <circle cx="12" cy="12" r="10" />
      <line x1="2" y1="12" x2="22" y2="12" />
      <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
    </svg>
  );
}
